/**
 * Firebase App Check initialization (client-only).
 * Import this only from client components; never from server or firebaseConfig.
 *
 * --- Unblocking Storage 412 (App Check) ---
 * If avatar/Storage uploads fail with HTTP 412 and the network tap (see networkTap.ts)
 * shows an App Check–related response body, use one of these (do not change Console automatically):
 *
 * Option A — Temporarily disable enforcement (fastest to verify):
 *   1. Firebase Console → Project → App Check.
 *   2. Find "Storage" (or "Cloud Storage") in the list.
 *   3. Set enforcement to "Unenforced" (or turn off) and save.
 *   Re-enforce once ReCaptcha/App Check is correctly configured.
 *
 * Option B — Enable App Check properly (recommended for production):
 *   1. Google reCAPTCHA Admin: create a reCAPTCHA v3 key, add your domains (localhost, Vercel domain).
 *   2. Firebase Console → App Check → your Web app → Register provider → reCAPTCHA v3,
 *      paste the reCAPTCHA v3 site key and (if asked) secret key.
 *   3. In Vercel (or .env): set VITE_RECAPTCHA_V3_SITE_KEY or NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY
 *      to the same reCAPTCHA v3 site key.
 *   4. For local dev: optionally set VITE_FIREBASE_APPCHECK_DEBUG=true and use a debug token
 *      (Firebase Console → App Check → Debug tokens); set FIREBASE_APPCHECK_DEBUG_TOKEN in the app (see below).
 */

import type { FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  getToken,
  ReCaptchaV3Provider,
  type AppCheck,
  type AppCheckTokenResult,
} from "firebase/app-check";

let appCheckInstance: AppCheck | null = null;

function getSiteKey(): string {
  const env = typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, unknown> }).env
    ? (import.meta as { env: Record<string, string> }).env
    : {};
  return (
    env.VITE_RECAPTCHA_V3_SITE_KEY ||
    env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY ||
    (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY) ||
    ""
  );
}

/**
 * Initialize Firebase App Check. Call once on the client after initializeApp(app).
 * Uses ReCaptchaV3Provider with site key from env; enables debug token when
 * NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG (or VITE_*) is "true" in non-production.
 * Build-safe: returns null when typeof window === "undefined" (SSR/build).
 */
export function initAppCheck(app: FirebaseApp | null | undefined): AppCheck | null {
  if (typeof window === "undefined") return null;
  if (appCheckInstance) return appCheckInstance;

  const siteKey = getSiteKey();
  if (!siteKey) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[AppCheck] No ReCaptcha v3 site key (VITE_RECAPTCHA_V3_SITE_KEY or NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY). App Check not initialized.");
    }
    return null;
  }

  // Debug token for localhost/dev (Firebase Console → App Check → Debug tokens)
  if (process.env.NODE_ENV !== "production") {
    const debugEnv =
      (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_FIREBASE_APPCHECK_DEBUG === "true") ||
      (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG === "true") ||
      (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG === "true");
    if (debugEnv) {
      (self as unknown as Record<string, boolean>)["FIREBASE_APPCHECK_DEBUG_TOKEN"] = true;
      console.log("[AppCheck] Debug token mode enabled (FIREBASE_APPCHECK_DEBUG_TOKEN = true). Register this token in Firebase Console → App Check → Debug tokens.");
    }
  }

  try {
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    if (process.env.NODE_ENV !== "production") {
      console.log("[AppCheck] Initialized with ReCaptcha v3.");
    }
    return appCheckInstance;
  } catch (e) {
    console.error("[AppCheck] initializeAppCheck failed:", e);
    return null;
  }
}

/**
 * Returns the App Check instance if initAppCheck was called on the client; otherwise null.
 */
export function getAppCheckInstance(): AppCheck | null {
  return appCheckInstance ?? null;
}

/**
 * Get current App Check token (for debug page). Returns redacted token prefix and optional expire info.
 */
export async function getAppCheckTokenDebug(): Promise<{
  tokenPrefix: string;
  expireTimeMillis: number | null;
  error: string | null;
}> {
  const appCheck = getAppCheckInstance();
  if (!appCheck) {
    return { tokenPrefix: "", expireTimeMillis: null, error: "App Check not initialized" };
  }
  try {
    const result: AppCheckTokenResult = await getToken(appCheck, false);
    const token = result?.token ?? "";
    const prefix = token.length > 20 ? `${token.slice(0, 20)}...` : token ? "[redacted]" : "";
    let expireTimeMillis: number | null = null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1] ?? "{}"));
      if (typeof payload.exp === "number") expireTimeMillis = payload.exp * 1000;
    } catch {
      // ignore
    }
    return { tokenPrefix: prefix, expireTimeMillis, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return { tokenPrefix: "", expireTimeMillis: null, error: message };
  }
}
